<template>
  <div class="card">
    <div class="card-body">
      <div class="mb-3 d-flex align-items-center" style="gap:8px;flex-wrap:wrap">
        <a-range-picker
          :placeholder="[$t('paiement.dateDebut'), $t('paiement.dateFin')]"
          format="DD/MM/YYYY"
          v-model="dateRangeData"
          :mode="mode"
          @change="(val) => { handleDateRange(val); }"
        />
        <a-tag color="green">{{ $t('caisse.typeEncaissement') }}</a-tag>
        <a-tag color="cyan">{{ $t('caisse.typeInverseEncaissement') }}</a-tag>
        <a-tag color="geekblue">{{ $t('caisse.typeAvoirCredit') }}</a-tag>
        <a-tag color="red">{{ $t('caisse.typeDecaissement') }}</a-tag>
        <!-- Caisses span school years; the feed shows every year unless filtered. -->
        <a-select
          :value="schoolarYearFilter"
          style="min-width: 170px"
          @change="onSchoolarYearFilterChange"
        >
          <a-select-option value="all">
            {{ $t("caisse.toutesLesAnnees") }}
          </a-select-option>
          <a-select-option
            v-for="sy in settings.schoolarYears"
            :key="sy._id"
            :value="sy._id"
          >
            {{ sy.name }}
          </a-select-option>
        </a-select>
      </div>

      <a-table
        @change="handleTableChange"
        rowKey="_rowKey"
        :pagination="tablePagination"
        :data-source="data"
        :columns="columns"
        :loading="tableLoading"
        :scroll="{ x: 'max-content' }"
        :row-class-name="rowClassName"
      >
        <!-- Type tag -->
        <template slot="rowType" slot-scope="text, record">
          <a-tag v-if="record._type === 'income'" color="green">
            {{ $t('caisse.typeEncaissement') }}
          </a-tag>
          <a-tag v-else-if="record._type === 'inverseIncome'" color="cyan">
            {{ $t('caisse.typeInverseEncaissement') }}
          </a-tag>
          <a-tag v-else-if="record._type === 'avoirCredit'" color="geekblue">
            {{ $t('caisse.typeAvoirCredit') }}
          </a-tag>
          <a-tag v-else color="red">
            {{ $t('caisse.typeDecaissement') }}
          </a-tag>
        </template>

        <!-- Serial / Receipt -->
        <template slot="serial" slot-scope="text, record">
          <template v-if="record._type === 'income'">
            <a-tag :color="getSerialColor(record)">{{ record.serial }}</a-tag>
            <br />
            <a-tag
              v-if="record.cancellation"
              :color="getCancellationColor(record.cancellation.type)"
            >{{ record.cancellation.type }}</a-tag>
          </template>
          <template v-else>
            <span class="text-muted" style="font-size:12px">{{ record.receiptNumber || '—' }}</span>
          </template>
        </template>

        <!-- Description -->
        <template slot="description" slot-scope="text, record">
          <template v-if="record._type === 'income'">
            <div v-for="i in record.details" :key="i._id" style="line-height:1.4">
              <span class="font-weight-bold">{{ i.studentName }}</span>
              <span class="text-muted ml-1" style="font-size:11px">{{ i.typeReglement }}</span>
              <div v-if="i.fees" style="margin-top:2px">
                <a-tag
                  color="blue"
                  v-for="(value, key) in i.fees"
                  :key="key"
                  style="font-size:10px;padding:0 4px;margin-bottom:2px"
                >{{ formatFeeName(key, i.daily) }}</a-tag>
              </div>
            </div>
          </template>
          <template v-else>
            <span class="font-weight-bold">{{ record._beneficiaryName || '—' }}</span>
            <div class="text-muted" style="font-size:11px">{{ record._label || '—' }}</div>
          </template>
        </template>

        <!-- Payment method -->
        <template slot="paymentMethod" slot-scope="text, record">
          <template v-if="record._type === 'income'">
            <div v-if="Array.isArray(record.paymentMethod)">
              <a-tooltip :title="$t('recette.detailsPaymentDist')">
                <a-button
                  type="primary"
                  shape="circle"
                  size="small"
                  @click.stop="chequeDetailsModal(record)"
                  style="margin-right:6px"
                >
                  <a-icon type="file-search" />
                </a-button>
              </a-tooltip>
              <span v-for="method in record.paymentMethod" :key="method">
                <a-tag :color="getPaymentMethodColor(method)" style="margin-bottom:4px">{{ method }}</a-tag>
              </span>
            </div>
            <div v-else>{{ text }}</div>
          </template>
          <template v-else-if="record._type === 'inverseIncome' || record._type === 'avoirCredit'">
            <template v-if="record._matchedSplits && record._matchedSplits.length">
              <a-tag
                v-for="(sp, i) in record._matchedSplits"
                :key="'ix-' + i"
                :color="getDepenseMethodColor(sp.method)"
                style="margin-bottom:2px"
              >
                {{ $t('decaissement.paymentMethod.' + sp.method) }}
              </a-tag>
            </template>
            <template v-else-if="record.methods && record.methods.length">
              <a-tag
                v-for="m in record.methods"
                :key="m"
                :color="getDepenseMethodColor(m)"
              >
                {{ $t('decaissement.paymentMethod.' + m) }}
              </a-tag>
            </template>
          </template>
          <template v-else>
            <!-- Show only the splits that matched this caisse -->
            <template v-if="record._matchedSplits && record._matchedSplits.length">
              <a-tag
                v-for="(sp, i) in record._matchedSplits"
                :key="i"
                :color="getDepenseMethodColor(sp.method)"
                style="margin-bottom:2px"
              >
                {{ $t('decaissement.paymentMethod.' + sp.method) }}
              </a-tag>
            </template>
            <template v-else-if="record.methods && record.methods.length">
              <a-tag
                v-for="m in record.methods"
                :key="m"
                :color="getDepenseMethodColor(m)"
              >
                {{ $t('decaissement.paymentMethod.' + m) }}
              </a-tag>
            </template>
          </template>
        </template>

        <!-- Amount — depense shows only the portion relevant to this caisse -->
        <template slot="amount" slot-scope="text, record">
          <span v-if="record._type === 'income'" class="amount-cell">
            {{ record.montantTotal != null ? record.montantTotal.toFixed(3) : '—' }}
          </span>
          <span v-else-if="record._type === 'inverseIncome' || record._type === 'avoirCredit'" class="amount-cell amount-inverse-income">
            {{ (record._amountForCaisse != null ? record._amountForCaisse : record.totalReceived || record.amount || 0).toFixed(3) }}
          </span>
          <span v-else class="amount-cell amount-depense">
            -{{ (record._amountForCaisse != null ? record._amountForCaisse : record.totalToPay || 0).toFixed(3) }}
          </span>
        </template>

        <!-- Date -->
        <template slot="date" slot-scope="text, record">
          {{ formatRowDate(record) }}
        </template>

        <!-- Done by -->
        <template slot="doneBy" slot-scope="text, record">
          <a-tag color="#2db7f5">
            <template v-if="record._type === 'income'">
              {{ (record.doneBy && record.doneBy.userName) || '—' }}
            </template>
            <template v-else-if="record._type === 'inverseIncome' || record._type === 'avoirCredit'">
              {{ record.receivedBy || record.createdBy || '—' }}
            </template>
            <template v-else>
              {{ record.createdBy || '—' }}
            </template>
          </a-tag>
        </template>

        <!-- Actions -->
        <template slot="actions" slot-scope="text, record">
          <a-button
            v-if="record._type === 'depense'"
            size="small"
            type="default"
            icon="file-search"
            @click="openDepenseDetail(record)"
          >
            {{ $t('caisse.details') }}
          </a-button>
          <a-button
            v-else-if="record._type === 'inverseIncome'"
            size="small"
            type="default"
            icon="file-search"
            @click="openInverseEncaissementDetail(record)"
          >
            {{ $t('caisse.details') }}
          </a-button>
          <a-button
            v-else-if="record._type === 'avoirCredit'"
            size="small"
            type="default"
            icon="file-search"
            @click="openAvoirCreditDetail(record)"
          >
            {{ $t('caisse.details') }}
          </a-button>
        </template>

        <!-- Filter dropdown -->
        <div
          slot="filterDropdown"
          slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }"
          style="padding:8px"
        >
          <a-input
            v-ant-ref="(c) => (searchInput = c)"
            :placeholder="`${$t('paiement.chercher')} ${column.title}`"
            :value="selectedKeys[0]"
            style="width:188px;margin-bottom:8px;display:block"
            @change="(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])"
            @pressEnter="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
          />
          <a-button
            type="primary"
            icon="search"
            size="small"
            style="width:90px;margin-right:8px"
            @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
          >{{ $t("scolarite.recherche") }}</a-button>
          <a-button size="small" style="width:90px" @click="() => handleReset(clearFilters)">
            {{ $t("action.reinitialiser") }}
          </a-button>
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
      </a-table>
    </div>

    <!-- Depense payment detail drawer (reused from Historique des paiements) -->
    <PaymentDetailDrawer
      :visible="drawerVisible"
      :paymentId="drawerPaymentId"
      @close="drawerVisible = false"
    />
    <EncaissementDetailDrawer
      :visible="encDrawerVisible"
      :schedule-id="encScheduleId"
      @close="encDrawerVisible = false"
    />

    <avoir-detail-drawer
      :visible="avoirDrawerVisible"
      :avoir-id="avoirDetailId"
      @close="onAvoirDrawerClose"
    />

    <chequeModal
      v-if="chequeVisible"
      :chequeVisible="chequeVisible"
      :invoiceId="invoiceId"
      @close="chequeVisible = false"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import apiClient from "@/services/axios";
import moment from "moment";
import AvoirDetailDrawer from "@/views/compV3/decaissement/avoir/components/AvoirDetailDrawer.vue";
import {
  formatFeeName,
  getSerialColor,
  getCancellationColor,
  getPaymentMethodColor,
} from "@/utilities/settings";
import chequeModal from "@/components/comptabilite/chequeModal";
import PaymentDetailDrawer from "@/views/compV3/decaissement/paymentHistory/PaymentDetailDrawer.vue";
import EncaissementDetailDrawer from "@/views/compV3/decaissement/encaissementValidation/EncaissementDetailDrawer.vue";

const DEPENSE_METHOD_COLORS = {
  cash: "green",
  cheque: "blue",
  virement: "purple",
  traite: "orange",
  tpe: "pink",
  prelevement: "cyan",
  mixed: "default",
};

export default {
  name: "CaisseInvoicesTable",
  components: { chequeModal, PaymentDetailDrawer, EncaissementDetailDrawer, AvoirDetailDrawer },
  props: {
    caisseId: { required: true },
  },
  computed: {
    ...mapState(["settings"]),
    tablePagination() {
      return {
        ...this.pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
      };
    },
    schoolarYearNameById() {
      return (this.settings.schoolarYears || []).reduce((acc, sy) => {
        acc[sy._id] = sy.name;
        return acc;
      }, {});
    },
    columns() {
      return [
        {
          title: this.$t("caisse.typeColonne"),
          key: "rowType",
          width: 115,
          scopedSlots: { customRender: "rowType" },
        },
        {
          // The caisse keeps its movements across years, so the year is shown per row.
          title: this.$t("caisse.anneeScolaireColonne"),
          key: "schoolarYear",
          width: 130,
          customRender: (text, record) =>
            this.schoolarYearNameById[record._schoolarYear] || "—",
        },
        {
          title: this.$t("recette.numSerie"),
          dataIndex: "serial",
          key: "serial",
          width: 110,
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "serial",
          },
          onFilter: (value, record) => {
            const h = record._type === "income" ? (record.serial || "") : (record.receiptNumber || "");
            return h.toLowerCase().includes(value.toLowerCase());
          },
          onFilterDropdownVisibleChange: (v) => {
            if (v) setTimeout(() => this.searchInput && this.searchInput.focus());
          },
        },
        {
          title: this.$t("caisse.descriptionColonne"),
          key: "description",
          ellipsis: true,
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "description",
          },
          onFilter: (value, record) => {
            if (record._type === "income") {
              return (record.details || []).some((i) =>
                (i.studentName || "").toLowerCase().includes(value.toLowerCase())
              );
            }
            return (
              (record._beneficiaryName || "").toLowerCase().includes(value.toLowerCase()) ||
              (record._label || "").toLowerCase().includes(value.toLowerCase())
            );
          },
          onFilterDropdownVisibleChange: (v) => {
            if (v) setTimeout(() => this.searchInput && this.searchInput.focus());
          },
        },
        {
          title: this.$t("recette.modePaiement"),
          key: "paymentMethod",
          width: 150,
          scopedSlots: { customRender: "paymentMethod" },
        },
        {
          title: this.$t("recette.montantTTC"),
          key: "amount",
          width: 130,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: this.$t("recette.datePaiement"),
          key: "date",
          width: 145,
          scopedSlots: { customRender: "date" },
        },
        {
          title: this.$t("recette.doneBy"),
          key: "doneBy",
          width: 130,
          scopedSlots: { customRender: "doneBy" },
        },
        {
          title: "",
          key: "actions",
          width: 90,
          scopedSlots: { customRender: "actions" },
        },
      ];
    },
  },
  watch: {
    caisseId() {
      this.pagination.current = 1;
      this.loadData();
    },
  },
  async created() {
    await this.loadData();
  },
  data() {
    return {
      dateRangeData: [],
      mode: ["date", "date"],
      schoolarYearFilter: "all",
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
      },
      tableLoading: true,
      data: [],
      chequeVisible: false,
      invoiceId: "",
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      drawerVisible: false,
      drawerPaymentId: null,
      encDrawerVisible: false,
      encScheduleId: null,
      avoirDrawerVisible: false,
      avoirDetailId: null,
    };
  },
  methods: {
    formatFeeName,
    getSerialColor,
    getCancellationColor,
    getPaymentMethodColor,
    getDepenseMethodColor(method) {
      return DEPENSE_METHOD_COLORS[method] || "default";
    },
    rowClassName(record) {
      if (record._type === "depense") return "row-depense";
      if (record._type === "inverseIncome") return "row-inverse-income";
      if (record._type === "avoirCredit") return "row-avoir-credit";
      return "";
    },
    formatRowDate(record) {
      const ms = this.caisseRowSortMs(record);
      return ms ? moment(ms).format("DD/MM/YYYY HH:mm") : "—";
    },
    caisseRowSortMs(row) {
      const ts = (v) => {
        if (v == null || v === "") return 0;
        const t = new Date(v).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      if (row._type === "income") {
        const d = ts(row.date);
        const c = ts(row.createdAt);
        if (d && c) return Math.max(d, c);
        return d || c;
      }
      if (row._type === "inverseIncome" || row._type === "avoirCredit") {
        const r = ts(row.receivedAt);
        const c = ts(row.createdAt);
        if (r && c) return Math.max(r, c);
        return r || c;
      }
      const p = ts(row.paidAt);
      const co = ts(row.createdAt);
      if (p && co) return Math.max(p, co);
      return p || co;
    },
    handleDateRange(value) {
      this.dateRangeData = value && value.length === 2 ? value : [];
      this.pagination.current = 1;
      this.loadData();
    },
    onSchoolarYearFilterChange(value) {
      this.schoolarYearFilter = value;
      this.pagination.current = 1;
      this.loadData();
    },
    handleTableChange(pag) {
      if (pag) {
        const pageChanged =
          pag.current !== this.pagination.current ||
          pag.pageSize !== this.pagination.pageSize;
        this.pagination.current = pag.current;
        this.pagination.pageSize = pag.pageSize;
        if (pageChanged) {
          this.loadData();
        }
      }
    },
    chequeDetailsModal(record) {
      this.invoiceId = record._id;
      this.chequeVisible = true;
    },
    openDepenseDetail(record) {
      this.drawerPaymentId = String(record._id);
      this.drawerVisible = true;
    },
    openInverseEncaissementDetail(record) {
      const ids = record.inverseScheduleIds || [];
      const sid = ids[0] != null ? String(ids[0]) : "";
      if (!sid) {
        this.$message.warning(this.$t("caisse.inverseDetailMissing"));
        return;
      }
      this.encScheduleId = sid;
      this.encDrawerVisible = true;
    },
    openAvoirCreditDetail(record) {
      const id = record._id != null ? String(record._id) : "";
      if (!id) return;
      this.avoirDetailId = id;
      this.avoirDrawerVisible = true;
    },
    onAvoirDrawerClose() {
      this.avoirDrawerVisible = false;
      this.avoirDetailId = null;
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },
    async loadData() {
      this.tableLoading = true;
      try {
        const params = {
          schoolarYear: this.schoolarYearFilter,
          page: this.pagination.current,
          pageSize: this.pagination.pageSize,
        };
        if (
          this.dateRangeData &&
          this.dateRangeData.length === 2 &&
          this.dateRangeData[0] &&
          this.dateRangeData[1]
        ) {
          params.dateFrom = moment(this.dateRangeData[0]).format("YYYY-MM-DD");
          params.dateTo = moment(this.dateRangeData[1]).format("YYYY-MM-DD");
        }
        const res = await apiClient.get(
          `/compV3/caisse/${this.caisseId}/transactions`,
          { params }
        );
        const { data, total, page, pageSize } = res.data;
        this.data = data || [];
        this.pagination.total = total;
        this.pagination.current = page;
        this.pagination.pageSize = pageSize;
      } catch (e) {
        console.error("Error loading caisse transactions:", e);
      } finally {
        this.tableLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.amount-cell {
  font-family: "Monaco", "Menlo", monospace;
  font-weight: 600;
  font-size: 13px;
}
.amount-depense {
  color: #cf1322;
}
.amount-inverse-income {
  color: #3f8600;
}
.font-weight-bold { font-weight: 600; }
.text-muted { color: rgba(0,0,0,0.45); }
</style>

<style>
.row-depense td {
  background-color: rgba(245, 34, 45, 0.04) !important;
}
.row-inverse-income td {
  background-color: rgba(19, 194, 194, 0.06) !important;
}
.row-avoir-credit td {
  background-color: rgba(47, 84, 235, 0.06) !important;
}
</style>
